import React, { useState, useRef, useEffect } from "react";
import {
  saveExam,
  updateExam,
  getAllExams,
  generateExamId,
  Exam,
  ExamMetadata,
} from "../services/examService";
import { parseExamContent } from "../services/aiService";
import { initExamUpload, processExamUpload, extractStructuredExam, getExamReviewQueue, approveExamUpload, rejectExamUpload } from "../services/examIngestionService";
import { auth, storage, db } from "../services/firebase";
import { SUBJECTS_BY_AREA, AREAS, BANCAS, SEED_EXAMS } from "../data/seedExams";
import { serverTimestamp, collection, getDocs, addDoc, updateDoc, doc, getDoc, query, where, orderBy, limit } from "firebase/firestore";
import {
  getUsersByExamId,
  batchRegenerateStudyPlans,
} from "../services/onboardingService";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { addAulaToExamTopic, getExamAulasCatalog, upsertExamAulas } from "../services/aulasService";
import { ExamAulasDoc } from "../services/aulasService";

const ADMIN_BUILD_VERSION = typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev";
const ADMIN_BUILD_TIME = typeof __APP_BUILD_TIME__ !== "undefined" ? __APP_BUILD_TIME__ : "";

declare global {
  interface Window {
    pdfjsLib: any;
  }
}

type TenantModuleKey =
  | "dashboard"
  | "studyPlan"
  | "approvalMap"
  | "simulados"
  | "performance"
  | "ranking"
  | "flashcards"
  | "savedExplanations"
  | "materials"
  | "aulas"
  | "profile";

const TENANT_MODULE_OPTIONS: Array<{ key: TenantModuleKey; label: string }> = [
  { key: "dashboard", label: "Questões (Área do Aluno)" },
  { key: "studyPlan", label: "Plano de Estudos" },
  { key: "approvalMap", label: "Mapa da Aprovação" },
  { key: "simulados", label: "Simulados" },
  { key: "performance", label: "Desempenho" },
  { key: "ranking", label: "Ranking" },
  { key: "flashcards", label: "Flashcards" },
  { key: "savedExplanations", label: "Explicações Salvas" },
  { key: "materials", label: "Materiais" },
  { key: "aulas", label: "Aulas" },
  { key: "profile", label: "Perfil" },
];

const DEFAULT_TENANT_MODULES: Record<TenantModuleKey, boolean> = {
  dashboard: true,
  studyPlan: true,
  approvalMap: true,
  simulados: true,
  performance: true,
  ranking: true,
  flashcards: true,
  savedExplanations: true,
  materials: true,
  aulas: true,
  profile: true,
};

const AdminPage: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [exams, setExams] = useState<Exam[]>([]);
  const [editingExamId, setEditingExamId] = useState<string | null>(null);
  const [view, setView] = useState<
    "list" | "users" | "form" | "seed" | "regenerate" | "guides" | "affiliates" | "tutorials" | "reviewQueue" | "aulas" | "tenants"
  >("list");
  const [seedingProgress, setSeedingProgress] = useState<{
    current: number;
    total: number;
    status: string;
  } | null>(null);
  const [regenerateProgress, setRegenerateProgress] = useState<{
    current: number;
    total: number;
    email: string;
  } | null>(null);
  const [selectedExamForRegenerate, setSelectedExamForRegenerate] =
    useState<Exam | null>(null);
  const [affectedUsersCount, setAffectedUsersCount] = useState<number>(0);
  const [planCounts, setPlanCounts] = useState<Record<string, number>>({});
  const [planCountsLoading, setPlanCountsLoading] = useState<boolean>(false);
  const [userStats, setUserStats] = useState<{
    total: number;
    paid: number;
    free: number;
    paidActive: number;
    paidExpired: number;
  }>({
    total: 0,
    paid: 0,
    free: 0,
    paidActive: 0,
    paidExpired: 0,
  });
  const [userStatsLoading, setUserStatsLoading] = useState<boolean>(false);
  const [userFilter, setUserFilter] = useState<"all" | "paid" | "free" | "expired">("all");
  const [userRows, setUserRows] = useState<Array<{
    id: string;
    email: string;
    plan: string;
    isPaid: boolean;
    status: "ativo" | "expirado" | "nao_pagante";
    expiresAt: string;
    billingCycle: string;
    paymentSource: string;
    lastPaymentAmount: number | null;
  }>>([]);
  const [tenants, setTenants] = useState<Array<{
    id: string;
    slug: string;
    displayName: string;
    status: "active" | "inactive";
    modules: Record<TenantModuleKey, boolean>;
  }>>([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [tenantSavingKey, setTenantSavingKey] = useState<string | null>(null);

  const [step, setStep] = useState<
    "area" | "metadata" | "subjects" | "content" | "review"
  >("area");
  const [guides, setGuides] = useState<any[]>([]);
  const [guideForm, setGuideForm] = useState({
    id: "",
    title: "",
    tag: "",
    price: "R$ 9,90",
    videoUrl: "",
    ebookUrl: "",
    thumb: "",
    previewUrl: "",
    checkoutUrl: "",
    purchased: false,
  });
  const [guideFile, setGuideFile] = useState<File | null>(null);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [guideUploading, setGuideUploading] = useState(false);
  const [guideLoading, setGuideLoading] = useState(false);
  const [guideError, setGuideError] = useState("");

  // Afiliados
  const [affiliates, setAffiliates] = useState<any[]>([]);
  const [affiliateLoading, setAffiliateLoading] = useState(false);
  const [affiliateForm, setAffiliateForm] = useState({
    name: "",
    email: "",
    cupom: "",
    percent: 25,
  });
  const [affiliateError, setAffiliateError] = useState<string>("");
  const [affiliateSales, setAffiliateSales] = useState<any[]>([]);
  const [affiliateSalesLoading, setAffiliateSalesLoading] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState<string | null>(null);

  // Tutoriais
  const [tutorials, setTutorials] = useState<any[]>([]);
  const [tutorialForm, setTutorialForm] = useState({ title: "", url: "" });
  const [tutorialLoading, setTutorialLoading] = useState(false);
  const [tutorialError, setTutorialError] = useState<string>("");
  const [reviewQueue, setReviewQueue] = useState<any[]>([]);
  const [reviewLoading, setReviewLoading] = useState(false);

  // Aulas
  const [aulasCatalog, setAulasCatalog] = useState<ExamAulasDoc[]>([]);
  const [aulasLoading, setAulasLoading] = useState(false);
  const [aulasError, setAulasError] = useState("");
  const [aulasExamId, setAulasExamId] = useState("pmerj-2026");
  const [aulasSubject, setAulasSubject] = useState("");
  const [aulasTopic, setAulasTopic] = useState("");
  const [aulasTitle, setAulasTitle] = useState("");
  const [aulasUrl, setAulasUrl] = useState("");
  const [aulasDuration, setAulasDuration] = useState("20-45 min");
  const [aulasTeacher, setAulasTeacher] = useState("Curadoria Concurseiro Pobre");

  const [loading, setLoading] = useState(false);
  const [processingStatus, setProcessingStatus] = useState("");
  const [processingInfo, setProcessingInfo] = useState<{ truncated: boolean; usedChars: number; totalChars: number } | null>(null);
  const [error, setError] = useState("");

  // Estado do formulário
  const [selectedArea, setSelectedArea] = useState<string>("");
  const [metadata, setMetadata] = useState<ExamMetadata>({
    institution: "",
    role: "",
    year: new Date().getFullYear(),
    banca: "",
    level: "medio",
    active: true,
    examDate: "",
    aliases: [],
  });

  const [rawContent, setRawContent] = useState("");
  const [processedSubjects, setProcessedSubjects] = useState<any[]>([]);
  const [useAiProcessing, setUseAiProcessing] = useState(false);
  const [detectedCargos, setDetectedCargos] = useState<any[]>([]);
  const [selectedCargoNames, setSelectedCargoNames] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Efeito para carregar os editais na visualização de lista
  useEffect(() => {
    if (view === "list" || view === "users") {
      const fetchExams = async () => {
        setLoading(true);
        setPlanCountsLoading(true);
        setUserStatsLoading(true);
        try {
          const examList = await getAllExams();
          setExams(examList);

          // Carrega contagem de planos por edital
          const entries = await Promise.all(
            examList.map(async (exam) => {
              try {
                const users = await getUsersByExamId(exam.id);
                return [exam.id, users.length] as [string, number];
              } catch (err) {
                console.error("Falha ao contar planos do edital", exam.id, err);
                return [exam.id, 0] as [string, number];
              }
            })
          );
          setPlanCounts(Object.fromEntries(entries));

          // Carrega visão de usuários: pagantes x não pagantes
          const usersSnap = await getDocs(collection(db, "users"));
          const now = Date.now();
          let paid = 0;
          let paidActive = 0;
          let paidExpired = 0;
          const rows: Array<{
            id: string;
            email: string;
            plan: string;
            isPaid: boolean;
            status: "ativo" | "expirado" | "nao_pagante";
            expiresAt: string;
            billingCycle: string;
            paymentSource: string;
            lastPaymentAmount: number | null;
          }> = [];

          usersSnap.forEach((u) => {
            const data: any = u.data() || {};
            const planRaw = String(data?.plan || data?.entitlements?.plan || "free");
            const plan = planRaw.toLowerCase();
            const isMarkedPaid =
              data?.isPro === true ||
              data?.entitlements?.isPro === true ||
              ["pro", "premium", "essencial", "nomeado"].includes(plan);

            const expiresAt = data?.expiresAt || data?.billing?.expiresAt || null;
            const expiresMs = expiresAt?.toMillis ? expiresAt.toMillis() : null;
            const hasActiveExpiry = expiresMs !== null && expiresMs > now;
            const isMarkedPaidNormalized = isMarkedPaid || hasActiveExpiry;
            const isExpired = isMarkedPaidNormalized && expiresMs !== null && expiresMs <= now;

            if (isMarkedPaidNormalized) {
              paid += 1;
              if (isExpired) {
                paidExpired += 1;
              } else {
                paidActive += 1;
              }
            }

            const displayPlan = isMarkedPaidNormalized && plan === "free" ? "pro" : planRaw;

            rows.push({
              id: u.id,
              email: String(data?.email || "(sem email)"),
              plan: displayPlan,
              isPaid: isMarkedPaidNormalized,
              status: !isMarkedPaidNormalized ? "nao_pagante" : isExpired ? "expirado" : "ativo",
              expiresAt: expiresMs ? new Date(expiresMs).toISOString() : "",
              billingCycle: String(data?.billingCycle || data?.billing?.billingCycle || "-"),
              paymentSource: String(data?.paymentSource || "-"),
              lastPaymentAmount: Number.isFinite(Number(data?.lastPaymentAmount)) ? Number(data.lastPaymentAmount) : null,
            });
          });

          const total = usersSnap.size;
          setUserStats({
            total,
            paid,
            free: Math.max(total - paid, 0),
            paidActive,
            paidExpired,
          });
          rows.sort((a, b) => {
            if (a.isPaid !== b.isPaid) return a.isPaid ? -1 : 1;
            return a.email.localeCompare(b.email);
          });
          setUserRows(rows);
        } catch (err) {
          setError("Falha ao carregar editais.");
          console.error(err);
        } finally {
          setLoading(false);
          setPlanCountsLoading(false);
          setUserStatsLoading(false);
        }
      };
      fetchExams();
    }
    if (view === "guides") {
      const fetchGuides = async () => {
        setGuideLoading(true);
        setGuideError("");
        try {
          const data = await import("../services/guidesService");
          const list = await data.getGuides();
          setGuides(list);
        } catch (err: any) {
          console.error(err);
          setGuideError("Falha ao carregar guias.");
        } finally {
          setGuideLoading(false);
        }
      };
      fetchGuides();
    }
    if (view === "affiliates") {
      const fetchAffiliates = async () => {
        setAffiliateLoading(true);
        setAffiliateError("");
        try {
          const snap = await getDocs(collection(db, "affiliates"));
          const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setAffiliates(items);
        } catch (err) {
          console.error("Erro ao carregar afiliados", err);
          setAffiliateError("Falha ao carregar afiliados.");
        } finally {
          setAffiliateLoading(false);
        }
      };
      fetchAffiliates();
    }

    if (view === "tutorials") {
      const fetchTutorials = async () => {
        setTutorialLoading(true);
        setTutorialError("");
        try {
          const q = query(collection(db, "tutorials"), orderBy("createdAt", "desc"));
          const snap = await getDocs(q);
          const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setTutorials(items);
        } catch (err) {
          console.error("Erro ao carregar tutoriais", err);
          setTutorialError("Falha ao carregar tutoriais.");
        } finally {
          setTutorialLoading(false);
        }
      };
      fetchTutorials();
    }

    if (view === "reviewQueue") {
      const fetchReviewQueue = async () => {
        setReviewLoading(true);
        try {
          const data = await getExamReviewQueue();
          setReviewQueue(Array.isArray(data?.items) ? data.items : []);
        } catch (err) {
          console.error("Erro ao carregar fila de revisão", err);
          setError("Falha ao carregar fila de revisão de editais.");
        } finally {
          setReviewLoading(false);
        }
      };
      fetchReviewQueue();
    }

    if (view === "aulas") {
      const fetchAulas = async () => {
        setAulasLoading(true);
        setAulasError("");
        try {
          const items = await getExamAulasCatalog();
          setAulasCatalog(items);
          if (items.length > 0 && !items.find((x) => x.id === aulasExamId)) {
            setAulasExamId(items[0].id);
          }
        } catch (err) {
          console.error("Erro ao carregar aulas", err);
          setAulasError("Falha ao carregar catálogo de aulas.");
        } finally {
          setAulasLoading(false);
        }
      };
      fetchAulas();
    }
  }, [view]);

  // Quando seleciona uma área, pré-popula as matérias
  useEffect(() => {
    if (selectedArea && SUBJECTS_BY_AREA[selectedArea] && !editingExamId) {
      setProcessedSubjects(
        SUBJECTS_BY_AREA[selectedArea].map((s) => ({ ...s })),
      );
    }
  }, [selectedArea, editingExamId]);

  // Verifica se é admin
  const user = auth.currentUser;
  const isAdmin =
    user?.email === "admin@concurseiropobre.com.br" ||
    user?.email === "felipe@example.com" ||
    user?.email?.includes("admin");

  const formatDateTime = (iso: string) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
  };

  const filteredUserRows = userRows.filter((row) => {
    if (userFilter === "paid") return row.isPaid;
    if (userFilter === "free") return !row.isPaid;
    if (userFilter === "expired") return row.status === "expirado";
    return true;
  });

  const handleExportUsersCsv = () => {
    if (!filteredUserRows.length) return;
    const headers = [
      "email",
      "plano",
      "pagante",
      "status",
      "expira_em",
      "ciclo",
      "origem_pagamento",
      "valor_ultimo_pagamento",
    ];
    const escape = (value: any) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const lines = filteredUserRows.map((row) => [
      row.email,
      row.plan,
      row.isPaid ? "sim" : "nao",
      row.status,
      row.expiresAt,
      row.billingCycle,
      row.paymentSource,
      row.lastPaymentAmount ?? "",
    ].map(escape).join(","));

    const csv = [headers.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `usuarios-admin-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!user) return <div className="p-8">Acesso negado. Faça login.</div>;

  const handleSaveGuide = async () => {
    setGuideLoading(true);
    setGuideError("");
    try {
      const data = await import("../services/guidesService");
      const id = guideForm.id || crypto.randomUUID();
      let ebookUrl = guideForm.ebookUrl;
      let previewUrl = guideForm.previewUrl;

      if (guideFile) {
        setGuideUploading(true);
        const storagePath = `ebooks/${id}/${guideFile.name}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, guideFile);
        ebookUrl = storagePath;
        setGuideUploading(false);
      }

      if (previewFile) {
        setGuideUploading(true);
        const previewPath = `ebooks/${id}/preview/${previewFile.name}`;
        const previewRef = ref(storage, previewPath);
        await uploadBytes(previewRef, previewFile);
        previewUrl = previewPath;
        setGuideUploading(false);
      }

      await data.saveGuide({ ...guideForm, id, ebookUrl, previewUrl });
      setGuideForm({
        id: "",
        title: "",
        tag: "",
        price: "R$ 9,90",
        videoUrl: "",
        ebookUrl: "",
        thumb: "",
        previewUrl: "",
        checkoutUrl: "",
        purchased: false,
      });
      setGuideFile(null);
      setPreviewFile(null);
      const list = await data.getGuides();
      setGuides(list);
    } catch (err: any) {
      console.error(err);
      setGuideError("Erro ao salvar guia");
    } finally {
      setGuideLoading(false);
      setGuideUploading(false);
    }
  };

  const handleDeleteGuide = async (id: string) => {
    if (!confirm("Excluir este guia?")) return;
    setGuideLoading(true);
    setGuideError("");
    try {
      const data = await import("../services/guidesService");
      await data.deleteGuide(id);
      const list = await data.getGuides();
      setGuides(list);
    } catch (err: any) {
      console.error(err);
      setGuideError("Erro ao excluir guia");
    } finally {
      setGuideLoading(false);
    }
  };

  // Afiliados
  const handleCreateAffiliate = async () => {
    const cupom = affiliateForm.cupom.trim().toUpperCase();
    const name = affiliateForm.name.trim();
    if (!cupom || !name) {
      setAffiliateError("Preencha nome e cupom.");
      return;
    }
    setAffiliateLoading(true);
    setAffiliateError("");
    try {
      await addDoc(collection(db, "affiliates"), {
        name,
        email: affiliateForm.email.trim(),
        cupom,
        percent: affiliateForm.percent || 25,
        active: true,
        createdAt: serverTimestamp(),
      });
      setAffiliateForm({ name: "", email: "", cupom: "", percent: 25 });
      const snap = await getDocs(collection(db, "affiliates"));
      setAffiliates(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Erro ao criar afiliado", err);
      setAffiliateError("Erro ao criar afiliado.");
    } finally {
      setAffiliateLoading(false);
    }
  };

  const handleToggleAffiliate = async (id: string, active: boolean) => {
    setAffiliateLoading(true);
    setAffiliateError("");
    try {
      await updateDoc(doc(db, "affiliates", id), { active: !active });
      const snap = await getDocs(collection(db, "affiliates"));
      setAffiliates(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Erro ao atualizar afiliado", err);
      setAffiliateError("Erro ao atualizar afiliado.");
    } finally {
      setAffiliateLoading(false);
    }
  };

  const handleViewAffiliateSales = async (affiliateCupom: string) => {
    setAffiliateSalesLoading(true);
    setAffiliateError("");
    setSelectedAffiliate(affiliateCupom);
    try {
      const q = query(
        collection(db, "affiliateSales"),
        where("affiliateId", "==", affiliateCupom),
        limit(100)
      );
      const snap = await getDocs(q);
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => {
        const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return tb - ta;
      });
      setAffiliateSales(items);
      if (items.length === 0) {
        setAffiliateError("Nenhuma venda encontrada para este afiliado.");
      }
    } catch (err) {
      console.error("Erro ao carregar vendas do afiliado", err);
      setAffiliateError("Falha ao carregar vendas do afiliado.");
    } finally {
      setAffiliateSalesLoading(false);
    }
  };

  const handleCreateTutorial = async () => {
    const title = tutorialForm.title.trim();
    const url = tutorialForm.url.trim();
    if (!title || !url) {
      setTutorialError("Preencha título e URL.");
      return;
    }
    setTutorialLoading(true);
    setTutorialError("");
    try {
      await addDoc(collection(db, "tutorials"), {
        title,
        url,
        createdAt: serverTimestamp(),
      });
      setTutorialForm({ title: "", url: "" });
      const snap = await getDocs(query(collection(db, "tutorials"), orderBy("createdAt", "desc")));
      setTutorials(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Erro ao criar tutorial", err);
      setTutorialError("Erro ao criar tutorial.");
    } finally {
      setTutorialLoading(false);
    }
  };

  const handleAddAulaLink = async () => {
    if (!aulasExamId || !aulasSubject.trim() || !aulasTopic.trim() || !aulasTitle.trim() || !aulasUrl.trim()) {
      setAulasError("Preencha concurso, matéria, tópico, título e URL.");
      return;
    }

    setAulasLoading(true);
    setAulasError("");
    try {
      const current = aulasCatalog.find((x) => x.id === aulasExamId) || {
        id: aulasExamId,
        name: aulasExamId.toUpperCase(),
        institution: "A definir",
        banca: "A definir",
        year: String(new Date().getFullYear()),
        status: "ativo",
        subjects: [],
      } as ExamAulasDoc;

      const updated = addAulaToExamTopic(current, aulasSubject.trim(), aulasTopic.trim(), {
        title: aulasTitle.trim(),
        url: aulasUrl.trim(),
        platform: "youtube",
        duration: aulasDuration.trim() || "20-45 min",
        teacher: aulasTeacher.trim() || "Curadoria Concurseiro Pobre",
        isFree: true,
      });

      await upsertExamAulas(updated);
      const items = await getExamAulasCatalog();
      setAulasCatalog(items);

      setAulasTitle("");
      setAulasUrl("");
    } catch (err) {
      console.error("Erro ao adicionar aula", err);
      setAulasError("Falha ao salvar aula.");
    } finally {
      setAulasLoading(false);
    }
  };

  // Função para popular o banco com editais pré-cadastrados
  const handleSeedDatabase = async () => {
    if (
      !confirm(
        `Isso irá adicionar ${SEED_EXAMS.length} editais ao banco de dados. Continuar?`,
      )
    ) {
      return;
    }

    setView("seed");
    setSeedingProgress({
      current: 0,
      total: SEED_EXAMS.length,
      status: "Iniciando...",
    });

    let created = 0;
    let errors = 0;

    for (let i = 0; i < SEED_EXAMS.length; i++) {
      const exam = SEED_EXAMS[i];
      const examId = generateExamId({
        institution: exam.institution,
        role: exam.role,
        year: exam.year,
        banca: exam.banca,
        level: exam.level,
        active: true,
      });

      setSeedingProgress({
        current: i + 1,
        total: SEED_EXAMS.length,
        status: `${exam.institution} - ${exam.role}`,
      });

      try {
        await saveExam({
          id: examId,
          metadata: {
            institution: exam.institution,
            role: exam.role,
            year: exam.year,
            banca: exam.banca,
            level: exam.level,
            active: true,
            examDate: "",
          },
          subjects: exam.subjects,
        });
        created++;
      } catch (err) {
        console.error(`Erro em ${exam.institution}:`, err);
        errors++;
      }

      // Pequeno delay para não sobrecarregar
      await new Promise((r) => setTimeout(r, 100));
    }

    setSeedingProgress({
      current: SEED_EXAMS.length,
      total: SEED_EXAMS.length,
      status: `Concluído! ${created} criados, ${errors} erros.`,
    });

    setTimeout(() => {
      setView("list");
      setSeedingProgress(null);
    }, 2000);
  };

  // Função para ler e extrair texto completo do PDF (premium/full-context)
  const extractTextFromPdf = async (file: File) => {
    if (!window.pdfjsLib) {
      throw new Error("Biblioteca PDF.js não carregada. Recarregue a página.");
    }

    const arrayBuffer = await file.arrayBuffer();
    const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    const maxPages = Math.min(pdf.numPages, 500);
    let fullText = "";

    setProcessingStatus(`Lendo edital completo: ${maxPages} páginas...`);

    for (let i = 1; i <= maxPages; i++) {
      if (i % 15 === 0) {
        setProcessingStatus(`Extraindo texto completo do edital (${i}/${maxPages})...`);
      }
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items
        .map((item: any) => item.str)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();

      fullText += `--- PÁGINA ${i} ---\n${pageText}\n\n`;
    }

    return fullText;
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setError("");
    setProcessingInfo(null);

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Faça login para processar o edital.");

      // 1) Upload do PDF para o storage
      setProcessingStatus("Enviando PDF para pipeline premium...");
      const storagePath = `exam-uploads/${currentUser.uid}/${Date.now()}-${file.name.replace(/\s+/g, '_')}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);

      // 2) Inicia processamento no backend
      setProcessingStatus("Inicializando ingestão do edital...");
      const init = await initExamUpload({
        userId: currentUser.uid,
        filePath: storagePath,
        fileName: file.name,
        fileSize: file.size,
      });

      setProcessingStatus("Extraindo texto e seções do edital (backend)...");
      const processed = await processExamUpload(init.uploadId);

      setProcessingStatus("Consolidando estrutura por IA (passada 2)...");
      const structured = await extractStructuredExam({ uploadId: init.uploadId, versionId: processed.versionId });

      // 3) Lê versão consolidada do Firestore e já aplica no formulário
      const versionSnap = await getDoc(doc(db, "exam_versions", processed.versionId));
      const versionData: any = versionSnap.exists() ? versionSnap.data() : null;

      const subjects = Array.isArray(versionData?.subjects)
        ? versionData.subjects.map((s: any) => ({
            name: s?.name || "Matéria",
            topics: Array.isArray(s?.topics) ? s.topics : [],
            confidence: s?.confidence || "medium",
          }))
        : [];

      if (subjects.length > 0) {
        setProcessedSubjects(subjects);
      }

      const cargos = Array.isArray(versionData?.cargos)
        ? versionData.cargos.filter((c: any) => c?.name)
        : [];
      setDetectedCargos(cargos);
      setSelectedCargoNames(cargos.map((c: any) => String(c.name)));

      const m = versionData?.metadata || {};
      setMetadata((prev) => ({
        ...prev,
        institution: prev.institution || m.institution || "",
        role: prev.role || m.role || "",
        banca: prev.banca || m.banca || "",
        year: prev.year || m.year || new Date().getFullYear(),
        examDate: prev.examDate || m.examDate || "",
      }));

      const rawChars = Number(versionData?.raw?.chars || 0);
      const usedChars = Number(versionData?.raw?.text?.length || 0);
      setProcessingInfo({
        truncated: false,
        usedChars,
        totalChars: rawChars || usedChars,
      });

      setProcessingStatus(
        `Pipeline premium concluída: ${subjects.length} matérias estruturadas (${structured.extractionMode}).`,
      );

      // mantém rawContent para debug/reprocessamento manual
      if (versionData?.raw?.text) {
        setRawContent(versionData.raw.text);
      }
    } catch (err: any) {
      console.error(err);
      // Fallback local para não bloquear operação
      try {
        setProcessingStatus("Pipeline backend falhou. Aplicando fallback local...");
        const text = await extractTextFromPdf(file);
        setRawContent(text);
        const result = await parseExamContent(text);
        if (result?.subjects?.length) setProcessedSubjects(result.subjects);
        if (Array.isArray(result?.cargos)) {
          const cargos = result.cargos.filter((c: any) => c?.name);
          setDetectedCargos(cargos);
          setSelectedCargoNames(cargos.map((c: any) => String(c.name)));
        }
        if (result?.examDate) setMetadata((prev) => ({ ...prev, examDate: prev.examDate || result.examDate || "" }));
        setProcessingInfo(result?.info || null);
        setProcessingStatus("Fallback local concluído. Revise os dados antes de salvar.");
      } catch (fallbackErr: any) {
        setError("Erro ao processar PDF: " + (fallbackErr?.message || err?.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAreaSelect = (areaId: string) => {
    setSelectedArea(areaId);
    setStep("metadata");
  };

  const handleMetadataSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!metadata.institution || !metadata.role || !metadata.banca) {
      setError("Preencha todos os campos obrigatórios.");
      return;
    }
    setError("");
    setStep("subjects");
  };

  const handleSubjectsSubmit = () => {
    if (processedSubjects.length === 0) {
      setError("Adicione pelo menos uma matéria.");
      return;
    }

    if (useAiProcessing) {
      setStep("content");
    } else {
      setStep("review");
    }
  };

  const handleProcessContent = async () => {
    if (!rawContent.trim()) {
      setError("Cole o conteúdo do edital ou faça upload do PDF.");
      return;
    }

    setLoading(true);
    setError("");
    setProcessingStatus("Enviando para o Professor Pobre (IA)...");

    try {
      const result = await parseExamContent(rawContent);

      if (!result.subjects || result.subjects.length === 0) {
        throw new Error("Nenhuma matéria identificada. Verifique o texto.");
      }

      setProcessedSubjects(result.subjects);
      if (Array.isArray(result?.cargos)) {
        const cargos = result.cargos.filter((c: any) => c?.name);
        setDetectedCargos(cargos);
        setSelectedCargoNames(cargos.map((c: any) => String(c.name)));
      }
      setProcessingInfo(result.info || null);

      if (result.examDate && !metadata.examDate) {
        setMetadata((prev) => ({ ...prev, examDate: result.examDate }));
      }

      setStep("review");
    } catch (err: any) {
      setProcessingInfo(null);
      setError(err.message || "Erro ao processar conteúdo.");
    } finally {
      setLoading(false);
      setProcessingStatus("");
    }
  };

  const loadTenants = async () => {
    setTenantsLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "tenants"), orderBy("slug", "asc")));
      const rows = snap.docs.map((d) => {
        const data: any = d.data() || {};
        return {
          id: d.id,
          slug: String(data?.slug || d.id),
          displayName: String(data?.displayName || data?.slug || d.id),
          status: data?.status === "inactive" ? "inactive" : "active",
          modules: {
            ...DEFAULT_TENANT_MODULES,
            ...(data?.modules || {}),
          } as Record<TenantModuleKey, boolean>,
        };
      });
      setTenants(rows);
    } catch (err) {
      console.error("Erro ao carregar tenants", err);
      setError("Falha ao carregar tenants.");
    } finally {
      setTenantsLoading(false);
    }
  };

  const handleToggleTenantModule = async (tenantId: string, moduleKey: TenantModuleKey, enabled: boolean) => {
    const opKey = `${tenantId}:${moduleKey}`;
    setTenantSavingKey(opKey);
    setTenants((prev) =>
      prev.map((t) =>
        t.id === tenantId ? { ...t, modules: { ...t.modules, [moduleKey]: enabled } } : t,
      ),
    );

    try {
      await updateDoc(doc(db, "tenants", tenantId), {
        [`modules.${moduleKey}`]: enabled,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error("Erro ao atualizar módulo do tenant", err);
      setError("Não foi possível atualizar o módulo do tenant.");
      await loadTenants();
    } finally {
      setTenantSavingKey(null);
    }
  };

  useEffect(() => {
    if (view === "tenants") {
      void loadTenants();
    }
  }, [view]);

  const handleAddSubject = () => {
    setProcessedSubjects((prev) => [...prev, { name: "", topics: [""] }]);
  };

  const handleRemoveSubject = (index: number) => {
    setProcessedSubjects((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubjectChange = (
    index: number,
    field: "name" | "topics",
    value: any,
  ) => {
    setProcessedSubjects((prev) =>
      prev.map((s, i) => {
        if (i !== index) return s;
        if (field === "name") return { ...s, name: value };
        if (field === "topics")
          return {
            ...s,
            topics: value.split("\n").filter((t: string) => t.trim()),
          };
        return s;
      }),
    );
  };

  const parseAliases = (aliases?: string[] | string): string[] => {
    if (!aliases) return [];
    if (Array.isArray(aliases)) return aliases.map((a) => a.trim()).filter(Boolean);
    return aliases
      .split(',')
      .map((a) => a.trim())
      .filter(Boolean);
  };

  const normalizeSubjectName = (name: string = "") =>
    name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();

  const getSubjectsForCargo = (cargo: any, allSubjects: any[]) => {
    const cargoSubjects = Array.isArray(cargo?.subjects) ? cargo.subjects : [];
    if (!cargoSubjects.length) return allSubjects;

    const allowed = new Set(cargoSubjects.map((s: string) => normalizeSubjectName(String(s))));
    const filtered = allSubjects.filter((s) => allowed.has(normalizeSubjectName(String(s?.name || ""))));
    return filtered.length > 0 ? filtered : allSubjects;
  };

  const handleToggleCargoSelection = (cargoName: string, checked: boolean) => {
    setSelectedCargoNames((prev) => {
      if (checked) return Array.from(new Set([...prev, cargoName]));
      return prev.filter((c) => c !== cargoName);
    });
  };

  const handleSelectAllCargos = () => {
    setSelectedCargoNames(detectedCargos.map((c: any) => String(c?.name || "")).filter(Boolean));
  };

  const handleClearCargoSelection = () => setSelectedCargoNames([]);

  const handleSaveExam = async () => {
    setLoading(true);
    try {
      const aliases = parseAliases(metadata.aliases);

      if (editingExamId) {
        const updateData: { [key: string]: any } = {
          "metadata.banca": metadata.banca,
          "metadata.level": metadata.level,
          "metadata.active": metadata.active,
          "metadata.examDate": metadata.examDate || null,
          "metadata.aliases": aliases,
          subjects: processedSubjects,
          area: selectedArea,
        };
        await updateExam(editingExamId, updateData);
        alert("Edital atualizado com sucesso!");
      } else {
        const cargosToSave = detectedCargos.filter((c: any) => selectedCargoNames.includes(String(c?.name || "")));

        if (cargosToSave.length > 0) {
          let created = 0;
          for (const cargo of cargosToSave) {
            const cargoName = String(cargo?.name || "").trim();
            if (!cargoName) continue;

            const cargoSubjects = getSubjectsForCargo(cargo, processedSubjects);
            const cargoMeta: ExamMetadata = {
              ...metadata,
              role: cargoName,
              aliases,
            };
            const examId = generateExamId(cargoMeta);
            const newExam: Omit<Exam, "createdAt" | "updatedAt"> = {
              id: examId,
              metadata: cargoMeta,
              subjects: cargoSubjects,
            };
            await saveExam(newExam);
            created++;
          }
          alert(`Editais derivados salvos com sucesso! Total: ${created}`);
        } else {
          const examId = generateExamId(metadata);
          const newExam: Omit<Exam, "createdAt" | "updatedAt"> = {
            id: examId,
            metadata: { ...metadata, aliases },
            subjects: processedSubjects,
          };
          await saveExam(newExam);
          alert("Edital salvo com sucesso!");
        }
      }

      resetForm();
      setView("list");
    } catch (err: any) {
      setError("Erro ao salvar no banco: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingExamId(null);
    setStep("area");
    setSelectedArea("");
    setMetadata({
      institution: "",
      role: "",
      year: new Date().getFullYear(),
      banca: "",
      level: "medio",
      active: true,
      examDate: "",
      aliases: [],
    });
    setRawContent("");
    setProcessedSubjects([]);
    setDetectedCargos([]);
    setSelectedCargoNames([]);
    setUseAiProcessing(false);
  };

  const handleEdit = (exam: Exam) => {
    setEditingExamId(exam.id);
    setMetadata(exam.metadata);
    setProcessedSubjects(exam.subjects || []);
    setDetectedCargos([]);
    setSelectedCargoNames([]);
    setSelectedArea((exam as any).area || "");
    setStep("metadata");
    setView("form");
  };

  const handleAddNew = () => {
    resetForm();
    setView("form");
  };

  const handleStartRegenerate = async (exam: Exam) => {
    setSelectedExamForRegenerate(exam);
    setLoading(true);
    setError("");

    try {
      const users = await getUsersByExamId(exam.id);
      setAffectedUsersCount(users.length);
      setView("regenerate");
    } catch (err: any) {
      setError(err.message || "Erro ao buscar usuários afetados.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmRegenerate = async () => {
    if (!selectedExamForRegenerate) return;

    setRegenerateProgress({
      current: 0,
      total: affectedUsersCount,
      email: "Iniciando...",
    });

    try {
      const result = await batchRegenerateStudyPlans(
        selectedExamForRegenerate.id,
        selectedExamForRegenerate.subjects,
        {
          institution: selectedExamForRegenerate.metadata.institution,
          role: selectedExamForRegenerate.metadata.role,
          banca: selectedExamForRegenerate.metadata.banca,
        },
        (current, total, email) => {
          setRegenerateProgress({ current, total, email });
        },
      );

      setRegenerateProgress({
        current: result.total,
        total: result.total,
        email: `✅ Concluído! ${result.success} atualizados, ${result.errors} erros.`,
      });

      setTimeout(() => {
        setView("list");
        setRegenerateProgress(null);
        setSelectedExamForRegenerate(null);
      }, 3000);
    } catch (err: any) {
      setError("Erro ao regenerar planos: " + err.message);
      setView("list");
    }
  };

  // Renderização
  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8">
      <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gray-900 px-6 md:px-8 py-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-4">
            {view === "form" ? (
              <button
                onClick={() => setView("list")}
                className="bg-gray-800 hover:bg-gray-700 text-white px-3 py-1 rounded-lg text-sm transition"
              >
                ← Voltar
              </button>
            ) : (
              <button
                onClick={onBack}
                className="bg-gray-800 hover:bg-gray-700 text-white px-3 py-1 rounded-lg text-sm transition"
              >
                ← Sair
              </button>
            )}
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-white">
                🕵️ Painel de Editais
              </h1>
              <p className="text-[11px] text-gray-300 mt-1">
                build v{ADMIN_BUILD_VERSION}
                {ADMIN_BUILD_TIME ? ` • ${new Date(ADMIN_BUILD_TIME).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })}` : ""}
              </p>
            </div>
          </div>
          {view === "form" && (
            <div className="flex items-center gap-2">
              {["area", "metadata", "subjects", "content", "review"].map(
                (s, i) => (
                  <div
                    key={s}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                      step === s
                        ? "bg-emerald-500 text-white"
                        : [
                              "area",
                              "metadata",
                              "subjects",
                              "content",
                              "review",
                            ].indexOf(step) > i
                          ? "bg-emerald-200 text-emerald-800"
                          : "bg-gray-700 text-gray-400"
                    }`}
                  >
                    {i + 1}
                  </div>
                ),
              )}
            </div>
          )}
        </div>

        <div className="p-6 md:p-8">
          {error && (
            <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6 border border-red-200">
              {error}
            </div>
          )}

          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => setView("list")}
              className={`px-4 py-2 rounded-xl text-sm font-bold ${view === "list" ? "bg-emerald-500 text-white" : "bg-white border border-gray-200 text-gray-700"}`}
            >
              Editais
            </button>
            <button
              onClick={() => setView("users")}
              className={`px-4 py-2 rounded-xl text-sm font-bold ${view === "users" ? "bg-emerald-500 text-white" : "bg-white border border-gray-200 text-gray-700"}`}
            >
              Usuários
            </button>
            <button
              onClick={() => {
                setEditingExamId(null);
                setView("form");
                setStep("area");
              }}
              className={`px-4 py-2 rounded-xl text-sm font-bold ${view === "form" ? "bg-emerald-500 text-white" : "bg-white border border-gray-200 text-gray-700"}`}
            >
              Novo Edital
            </button>
            <button
              onClick={() => setView("seed")}
              className={`px-4 py-2 rounded-xl text-sm font-bold ${view === "seed" ? "bg-emerald-500 text-white" : "bg-white border border-gray-200 text-gray-700"}`}
            >
              Popular Banco
            </button>
            <button
              onClick={() => setView("guides")}
              className={`px-4 py-2 rounded-xl text-sm font-bold ${view === "guides" ? "bg-amber-500 text-white" : "bg-white border border-gray-200 text-gray-700"}`}
            >
              Guias Estratégicos
            </button>
            <button
              onClick={() => setView("affiliates")}
              className={`px-4 py-2 rounded-xl text-sm font-bold ${view === "affiliates" ? "bg-emerald-500 text-white" : "bg-white border border-gray-200 text-gray-700"}`}
            >
              Afiliados
            </button>
            <button
              onClick={() => setView("tutorials")}
              className={`px-4 py-2 rounded-xl text-sm font-bold ${view === "tutorials" ? "bg-blue-600 text-white" : "bg-white border border-gray-200 text-gray-700"}`}
            >
              Tutoriais
            </button>
            <button
              onClick={() => setView("aulas")}
              className={`px-4 py-2 rounded-xl text-sm font-bold ${view === "aulas" ? "bg-cyan-600 text-white" : "bg-white border border-gray-200 text-gray-700"}`}
            >
              Aulas
            </button>
            <button
              onClick={() => setView("reviewQueue")}
              className={`px-4 py-2 rounded-xl text-sm font-bold ${view === "reviewQueue" ? "bg-indigo-600 text-white" : "bg-white border border-gray-200 text-gray-700"}`}
            >
              Revisão PDF
            </button>
            <button
              onClick={() => setView("tenants")}
              className={`px-4 py-2 rounded-xl text-sm font-bold ${view === "tenants" ? "bg-violet-600 text-white" : "bg-white border border-gray-200 text-gray-700"}`}
            >
              Tenants / Módulos
            </button>
          </div>

          {/* VIEW: TENANTS */}
          {view === "tenants" && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                <h2 className="text-lg font-bold text-gray-900">Tenants ativos e módulos visíveis</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Ative/desative módulos por tenant. O menu no ambiente white-label respeita exatamente essa configuração.
                </p>
              </div>

              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                {tenantsLoading ? (
                  <p className="text-sm text-gray-500">Carregando tenants...</p>
                ) : tenants.length === 0 ? (
                  <p className="text-sm text-gray-500">Nenhum tenant cadastrado ainda.</p>
                ) : (
                  <div className="space-y-4">
                    {tenants.map((tenant) => (
                      <div key={tenant.id} className="border rounded-xl p-4 bg-gray-50">
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                          <div>
                            <p className="font-black text-gray-900">{tenant.displayName}</p>
                            <p className="text-xs text-gray-600">slug: {tenant.slug} • doc: {tenant.id}</p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${tenant.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-gray-200 text-gray-700"}`}>
                            {tenant.status === "active" ? "Ativo" : "Inativo"}
                          </span>
                        </div>

                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {TENANT_MODULE_OPTIONS.map((mod) => {
                            const checked = !!tenant.modules?.[mod.key];
                            const saving = tenantSavingKey === `${tenant.id}:${mod.key}`;
                            return (
                              <label key={mod.key} className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={saving}
                                  onChange={(e) => void handleToggleTenantModule(tenant.id, mod.key, e.target.checked)}
                                />
                                <span className="text-gray-800">{mod.label}</span>
                                {saving && <span className="text-xs text-gray-500 ml-auto">salvando...</span>}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* VIEW: AFILIADOS */}
          {view === "affiliates" && (
            <div className="space-y-4">
              {affiliateError && (
                <div className="bg-red-50 text-red-700 p-3 rounded border border-red-200 text-sm">
                  {affiliateError}
                </div>
              )}
              <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
                <h2 className="text-lg font-bold text-gray-800">Criar Afiliado</h2>
                <div className="grid md:grid-cols-2 gap-3">
                  <input
                    value={affiliateForm.name}
                    onChange={(e) => setAffiliateForm({ ...affiliateForm, name: e.target.value })}
                    placeholder="Nome"
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <input
                    value={affiliateForm.email}
                    onChange={(e) => setAffiliateForm({ ...affiliateForm, email: e.target.value })}
                    placeholder="Email (opcional)"
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <input
                    value={affiliateForm.cupom}
                    onChange={(e) => setAffiliateForm({ ...affiliateForm, cupom: e.target.value.toUpperCase() })}
                    placeholder="Cupom (ex: PROF-PAULA)"
                    className="w-full px-3 py-2 border rounded-lg uppercase"
                  />
                  <input
                    type="number"
                    value={affiliateForm.percent}
                    onChange={(e) => setAffiliateForm({ ...affiliateForm, percent: Number(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg"
                    min={0}
                    max={100}
                    step={1}
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={handleCreateAffiliate}
                    disabled={affiliateLoading}
                    className="px-4 py-2 bg-emerald-500 text-white rounded-xl font-bold disabled:opacity-50"
                  >
                    {affiliateLoading ? "Salvando..." : "Salvar afiliado"}
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold">Lista de afiliados</h3>
                  {affiliateLoading && <span className="text-xs text-gray-500">Carregando...</span>}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="py-2">Nome</th>
                        <th className="py-2">Cupom</th>
                        <th className="py-2">Email</th>
                        <th className="py-2">% Comissão</th>
                        <th className="py-2">Status</th>
                        <th className="py-2 text-right">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {affiliates.map((aff) => (
                        <tr key={aff.id} className="border-b last:border-none">
                          <td className="py-2 font-bold text-gray-800">{aff.name}</td>
                          <td className="py-2 uppercase">{aff.cupom}</td>
                          <td className="py-2 text-gray-600">{aff.email || "-"}</td>
                          <td className="py-2">{aff.percent || 25}%</td>
                          <td className="py-2">
                            <span className={`px-2 py-1 text-xs rounded-full font-bold ${aff.active ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                              {aff.active ? "Ativo" : "Inativo"}
                            </span>
                          </td>
                          <td className="py-2 text-right space-x-3">
                            <button
                              onClick={() => {
                                const url = `${window.location.origin}/checkout?cupom=${encodeURIComponent(aff.cupom || '')}`;
                                navigator.clipboard.writeText(url).then(() => {
                                  setAffiliateError(`Link copiado: ${url}`);
                                  setTimeout(() => setAffiliateError(""), 2000);
                                }).catch(() => setAffiliateError("Não foi possível copiar o link"));
                              }}
                              className="text-sm font-bold text-indigo-600 hover:text-indigo-800"
                              disabled={affiliateLoading}
                            >
                              Copiar link
                            </button>
                            <button
                              onClick={() => handleViewAffiliateSales(aff.cupom)}
                              className="text-sm font-bold text-amber-600 hover:text-amber-800"
                              disabled={affiliateLoading}
                            >
                              Ver vendas
                            </button>
                            <button
                              onClick={() => handleToggleAffiliate(aff.id, aff.active)}
                              className="text-sm font-bold text-emerald-600 hover:text-emerald-800"
                              disabled={affiliateLoading}
                            >
                              {aff.active ? "Desativar" : "Ativar"}
                            </button>
                          </td>
                        </tr>
                      ))}
                      {affiliates.length === 0 && !affiliateLoading && (
                        <tr>
                          <td colSpan={6} className="py-4 text-center text-gray-500">
                            Nenhum afiliado cadastrado.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {selectedAffiliate && (
                  <div className="mt-6 bg-white rounded-2xl border border-amber-100 p-4 space-y-3 shadow-sm">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs uppercase text-amber-600 font-black">Cupom</p>
                        <h3 className="text-lg font-bold text-gray-900">{selectedAffiliate}</h3>
                      </div>
                      <div className="text-right text-sm text-gray-500">
                        {affiliateSalesLoading ? "Carregando vendas..." : `${affiliateSales.length} vendas encontradas`}
                      </div>
                    </div>

                    {affiliateError && (
                      <div className="bg-red-50 text-red-700 p-3 rounded border border-red-200 text-sm">
                        {affiliateError}
                      </div>
                    )}

                    {!affiliateSalesLoading && affiliateSales.length > 0 && (
                      (() => {
                        const totalAmount = affiliateSales.reduce((sum, s) => sum + (s.amount || 0), 0);
                        const totalCommission = affiliateSales.reduce((sum, s) => sum + (s.commission || 0), 0);
                        return (
                          <div className="grid md:grid-cols-3 gap-3 text-sm">
                            <div className="p-3 rounded-xl bg-amber-50 border border-amber-100">
                              <p className="text-amber-700 font-semibold">Vendas</p>
                              <p className="text-2xl font-black text-amber-900">{affiliateSales.length}</p>
                            </div>
                            <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-100">
                              <p className="text-emerald-700 font-semibold">Faturado</p>
                              <p className="text-2xl font-black text-emerald-900">R$ {totalAmount.toFixed(2)}</p>
                            </div>
                            <div className="p-3 rounded-xl bg-indigo-50 border border-indigo-100">
                              <p className="text-indigo-700 font-semibold">Comissão (25%)</p>
                              <p className="text-2xl font-black text-indigo-900">R$ {totalCommission.toFixed(2)}</p>
                            </div>
                          </div>
                        );
                      })()
                    )}

                    <div className="overflow-x-auto border border-gray-100 rounded-xl">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-gray-500 bg-gray-50">
                            <th className="py-2 px-3">Data</th>
                            <th className="py-2 px-3">Pagamento</th>
                            <th className="py-2 px-3">Valor</th>
                            <th className="py-2 px-3">Desconto</th>
                            <th className="py-2 px-3">Comissão</th>
                            <th className="py-2 px-3">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {affiliateSalesLoading && (
                            <tr><td className="py-4 px-3 text-center text-gray-500" colSpan={6}>Carregando...</td></tr>
                          )}
                          {!affiliateSalesLoading && affiliateSales.length === 0 && (
                            <tr><td className="py-4 px-3 text-center text-gray-500" colSpan={6}>Nenhuma venda ainda.</td></tr>
                          )}
                          {!affiliateSalesLoading && affiliateSales.map((sale) => {
                            const date = sale.createdAt?.toDate ? sale.createdAt.toDate() : null;
                            const dateStr = date ? date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }) : '-';
                            return (
                              <tr key={sale.id} className="border-t">
                                <td className="py-2 px-3 text-gray-700">{dateStr}</td>
                                <td className="py-2 px-3 text-gray-700">{sale.paymentId || '-'}</td>
                                <td className="py-2 px-3 font-semibold text-gray-900">R$ {(sale.amount || 0).toFixed(2)}</td>
                                <td className="py-2 px-3 text-gray-700">{sale.discount_percent ? `${sale.discount_percent}%` : '—'}</td>
                                <td className="py-2 px-3 font-semibold text-indigo-700">R$ {(sale.commission || 0).toFixed(2)}</td>
                                <td className="py-2 px-3">
                                  <span className={`px-2 py-1 text-xs rounded-full font-bold ${sale.status === 'pending' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
                                    {sale.status || 'pending'}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* VIEW: AULAS */}
          {view === "aulas" && (
            <div className="space-y-4">
              {aulasError && (
                <div className="bg-red-50 text-red-700 p-3 rounded border border-red-200 text-sm">{aulasError}</div>
              )}

              <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
                <h2 className="text-lg font-bold text-gray-800">Adicionar aula por tópico (Firestore)</h2>
                <div className="grid md:grid-cols-2 gap-3">
                  <select value={aulasExamId} onChange={(e) => setAulasExamId(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                    {(aulasCatalog.length ? aulasCatalog : [{ id: "pmerj-2026", name: "PMERJ - Soldado" } as any]).map((exam) => (
                      <option key={exam.id} value={exam.id}>{exam.name}</option>
                    ))}
                  </select>
                  <input value={aulasSubject} onChange={(e) => setAulasSubject(e.target.value)} placeholder="Matéria (ex: Direito Penal)" className="w-full px-3 py-2 border rounded-lg" />
                  <input value={aulasTopic} onChange={(e) => setAulasTopic(e.target.value)} placeholder="Tópico (ex: Parte Geral)" className="w-full px-3 py-2 border rounded-lg" />
                  <input value={aulasTitle} onChange={(e) => setAulasTitle(e.target.value)} placeholder="Título da aula" className="w-full px-3 py-2 border rounded-lg" />
                  <input value={aulasUrl} onChange={(e) => setAulasUrl(e.target.value)} placeholder="URL (YouTube)" className="w-full px-3 py-2 border rounded-lg md:col-span-2" />
                  <input value={aulasDuration} onChange={(e) => setAulasDuration(e.target.value)} placeholder="Duração (ex: 35 min)" className="w-full px-3 py-2 border rounded-lg" />
                  <input value={aulasTeacher} onChange={(e) => setAulasTeacher(e.target.value)} placeholder="Professor/Canal" className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div className="flex justify-end">
                  <button onClick={handleAddAulaLink} disabled={aulasLoading} className="px-4 py-2 bg-cyan-600 text-white rounded-xl font-bold disabled:opacity-50">
                    {aulasLoading ? "Salvando..." : "Salvar aula"}
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                <h3 className="text-lg font-bold mb-3">Catálogo atual</h3>
                {aulasCatalog.length === 0 ? (
                  <p className="text-sm text-gray-500">Nenhum concurso com aulas no Firestore ainda (usando mock no app).</p>
                ) : (
                  <div className="space-y-3">
                    {aulasCatalog.map((exam) => (
                      <div key={exam.id} className="border rounded-xl p-3 bg-gray-50">
                        <p className="font-bold text-gray-900">{exam.name} <span className="text-xs text-gray-500">({exam.id})</span></p>
                        <p className="text-xs text-gray-600 mt-1">{(exam.subjects || []).length} matéria(s)</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* VIEW: TUTORIAIS */}
          {view === "tutorials" && (
            <div className="space-y-4">
              {tutorialError && (
                <div className="bg-red-50 text-red-700 p-3 rounded border border-red-200 text-sm">
                  {tutorialError}
                </div>
              )}
              <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
                <h2 className="text-lg font-bold text-gray-800">Cadastrar Tutorial</h2>
                <div className="grid md:grid-cols-2 gap-3">
                  <input
                    value={tutorialForm.title}
                    onChange={(e) => setTutorialForm({ ...tutorialForm, title: e.target.value })}
                    placeholder="Título"
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <input
                    value={tutorialForm.url}
                    onChange={(e) => setTutorialForm({ ...tutorialForm, url: e.target.value })}
                    placeholder="URL do YouTube (embed ou watch)"
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={handleCreateTutorial}
                    disabled={tutorialLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-xl font-bold disabled:opacity-50"
                  >
                    {tutorialLoading ? "Salvando..." : "Salvar tutorial"}
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold">Tutoriais cadastrados</h3>
                  {tutorialLoading && <span className="text-xs text-gray-500">Carregando...</span>}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="py-2">Título</th>
                        <th className="py-2">URL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tutorials.map((tut) => (
                        <tr key={tut.id} className="border-b last:border-none">
                          <td className="py-2 font-bold text-gray-800">{tut.title}</td>
                          <td className="py-2 text-gray-600 break-all">
                            <a href={tut.url} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline">{tut.url}</a>
                          </td>
                        </tr>
                      ))}
                      {tutorials.length === 0 && !tutorialLoading && (
                        <tr>
                          <td colSpan={2} className="py-4 text-center text-gray-500">
                            Nenhum tutorial cadastrado.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* VIEW: REVIEW QUEUE */}
          {view === "reviewQueue" && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Fila de revisão de editais (PDF)</h2>
                  <p className="text-sm text-gray-600">Itens pendentes de aprovação no Admin.</p>
                </div>
                <button
                  onClick={() => setView("reviewQueue")}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-bold text-sm"
                >
                  Atualizar
                </button>
              </div>

              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                {reviewLoading ? (
                  <p className="text-sm text-gray-500">Carregando fila...</p>
                ) : reviewQueue.length === 0 ? (
                  <p className="text-sm text-gray-500">Nenhum edital pendente de revisão.</p>
                ) : (
                  <div className="space-y-3">
                    {reviewQueue.map((item) => {
                      const cargos = Array.isArray(item.cargos) ? item.cargos : [];
                      return (
                        <div key={item.uploadId} className="border rounded-xl p-3 bg-gray-50">
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <div>
                              <p className="font-bold text-gray-900">{item.fileName || item.uploadId}</p>
                              <p className="text-xs text-gray-600">Upload: {item.uploadId} • Versão: {item.versionId}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {item?.reviewRequired && (
                                <span className="text-xs px-2 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200 font-bold">
                                  Revisão obrigatória
                                </span>
                              )}
                              <span className="text-xs px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 font-bold">
                                Confiança global: {typeof item.confidenceGlobal === "number" ? item.confidenceGlobal.toFixed(2) : "n/a"}
                              </span>
                            </div>
                          </div>

                          <div className="text-sm text-gray-700 mb-2">
                            Cargo escolhido pelo usuário: <strong>{item.selectedCargo || "não informado"}</strong>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3 text-xs">
                            <div className="bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700">
                              <span className="font-semibold">Quality score:</span>{" "}
                              {typeof item?.qualityScore?.score === "number" ? `${item.qualityScore.score}/100` : "n/a"}
                            </div>
                            <div className="bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700">
                              <span className="font-semibold">Quality gate:</span>{" "}
                              {item?.qualityGate?.passed ? "PASS" : "FAIL"}
                            </div>
                            <div className="bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 md:col-span-2">
                              <span className="font-semibold">Review status:</span>{" "}
                              {item?.reviewStatus || "pending_admin"}
                            </div>
                            {Array.isArray(item?.reviewReasons) && item.reviewReasons.length > 0 && (
                              <div className="bg-rose-50 border border-rose-200 rounded-lg px-2 py-1.5 text-rose-800 md:col-span-2">
                                <p className="font-semibold mb-1">Motivos da revisão obrigatória:</p>
                                <ul className="list-disc pl-5 space-y-0.5">
                                  {item.reviewReasons.slice(0, 5).map((reason: string, idx: number) => (
                                    <li key={idx}>{reason}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            <div className="bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 md:col-span-2">
                              <span className="font-semibold">Última telemetria:</span>{" "}
                              {item?.telemetryLast?.step || "n/a"}
                              {typeof item?.telemetryLast?.durationMs === "number" ? ` • ${item.telemetryLast.durationMs}ms` : ""}
                              {item?.telemetryLast?.source ? ` • source: ${item.telemetryLast.source}` : ""}
                              {item?.telemetryLast?.fallback ? ` • fallback: ${item.telemetryLast.fallback}` : ""}
                              {item?.telemetryLast?.error ? ` • erro: ${item.telemetryLast.error}` : ""}
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2 mb-3">
                            {cargos.slice(0, 8).map((cargo: any, idx: number) => (
                              <span key={idx} className="px-2 py-1 rounded-full text-xs bg-white border border-gray-200 text-gray-700">
                                {cargo?.name || `Cargo ${idx + 1}`}
                                {cargo?.confidence ? ` • ${cargo.confidence}` : ""}
                              </span>
                            ))}
                            {cargos.length > 8 && (
                              <span className="px-2 py-1 rounded-full text-xs bg-white border border-gray-200 text-gray-600">+{cargos.length - 8} cargos</span>
                            )}
                          </div>

                          <div className="flex justify-end gap-2">
                            <button
                              onClick={async () => {
                                try {
                                  setReviewLoading(true);
                                  const confirmed = window.confirm(`Tem certeza que deseja REPROVAR o upload ${item.uploadId}?`);
                                  if (!confirmed) return;
                                  await rejectExamUpload({ uploadId: item.uploadId });
                                  const data = await getExamReviewQueue();
                                  setReviewQueue(Array.isArray(data?.items) ? data.items : []);
                                } catch (e: any) {
                                  setError(`Falha ao reprovar upload ${item.uploadId}: ${e?.message || e}`);
                                } finally {
                                  setReviewLoading(false);
                                }
                              }}
                              className="px-3 py-1.5 rounded-lg bg-rose-600 text-white text-sm font-bold"
                            >
                              Reprovar
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  setReviewLoading(true);
                                  const names = cargos.map((c: any) => String(c?.name || "")).filter(Boolean);
                                  await approveExamUpload({ uploadId: item.uploadId, approvedCargoNames: names });
                                  const data = await getExamReviewQueue();
                                  setReviewQueue(Array.isArray(data?.items) ? data.items : []);
                                } catch (e: any) {
                                  setError(`Falha ao aprovar upload ${item.uploadId}: ${e?.message || e}`);
                                } finally {
                                  setReviewLoading(false);
                                }
                              }}
                              className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-bold"
                            >
                              Aprovar
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* VIEW: GUIAS */}
          {view === "guides" && (
            <div className="space-y-6">
              {guideError && (
                <div className="bg-red-50 text-red-700 p-3 rounded border border-red-200 text-sm">
                  {guideError}
                </div>
              )}
              <div className="bg-white rounded-2xl border border-gray-200 p-4 flex flex-col md:flex-row gap-4">
                <div className="flex-1 space-y-3">
                  <h2 className="text-lg font-bold text-gray-800">
                    Cadastrar/Editar Guia
                  </h2>
                  <input
                    value={guideForm.title}
                    onChange={(e) =>
                      setGuideForm({ ...guideForm, title: e.target.value })
                    }
                    placeholder="Título"
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      value={guideForm.tag}
                      onChange={(e) =>
                        setGuideForm({ ...guideForm, tag: e.target.value })
                      }
                      placeholder="Tag/Matéria"
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                    <input
                      value={guideForm.price}
                      onChange={(e) =>
                        setGuideForm({ ...guideForm, price: e.target.value })
                      }
                      placeholder="Preço (ex: R$ 9,90)"
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <input
                    value={guideForm.videoUrl}
                    onChange={(e) =>
                      setGuideForm({ ...guideForm, videoUrl: e.target.value })
                    }
                    placeholder="Video URL (YouTube embed)"
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-center">
                    <div className="space-y-1">
                      <label className="text-xs text-gray-600 font-semibold">
                        Upload Preview (PDF)
                      </label>
                      <input
                        type="file"
                        accept="application/pdf"
                        onChange={(e) =>
                          setPreviewFile(e.target.files?.[0] || null)
                        }
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs text-gray-600 font-semibold">
                        Ou use um caminho/URL
                      </label>
                      <input
                        value={guideForm.previewUrl}
                        onChange={(e) =>
                          setGuideForm({
                            ...guideForm,
                            previewUrl: e.target.value,
                          })
                        }
                        placeholder="ebooks/id/preview/amostra.pdf"
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </div>
                  <input
                    value={guideForm.checkoutUrl}
                    onChange={(e) =>
                      setGuideForm({
                        ...guideForm,
                        checkoutUrl: e.target.value,
                      })
                    }
                    placeholder="Checkout URL (Mercado Pago/Stripe)"
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-center">
                    <div className="space-y-1">
                      <label className="text-xs text-gray-600 font-semibold">
                        Upload PDF
                      </label>
                      <input
                        type="file"
                        accept="application/pdf"
                        onChange={(e) =>
                          setGuideFile(e.target.files?.[0] || null)
                        }
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs text-gray-600 font-semibold">
                        Ou use um caminho/URL
                      </label>
                      <input
                        value={guideForm.ebookUrl}
                        onChange={(e) =>
                          setGuideForm({
                            ...guideForm,
                            ebookUrl: e.target.value,
                          })
                        }
                        placeholder="ebooks/ebook_guia/guia.pdf"
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </div>
                  <input
                    value={guideForm.thumb}
                    onChange={(e) =>
                      setGuideForm({ ...guideForm, thumb: e.target.value })
                    }
                    placeholder="Thumb (ex: https://img.youtube.com/vi/ID/hqdefault.jpg)"
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <div className="flex gap-2">
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 text-sm text-gray-600">
                        <input
                          type="checkbox"
                          checked={guideForm.purchased}
                          onChange={(e) =>
                            setGuideForm({
                              ...guideForm,
                              purchased: e.target.checked,
                            })
                          }
                        />
                        Marcar como comprado (teste)
                      </label>
                    </div>
                    <button
                      onClick={handleSaveGuide}
                      disabled={guideLoading || guideUploading}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2 px-4 rounded-lg transition"
                    >
                      {guideUploading
                        ? "Enviando PDF..."
                        : guideLoading
                          ? "Salvando..."
                          : "Salvar Guia"}
                    </button>
                    <button
                      onClick={() => {
                        setGuideForm({
                          id: "",
                          title: "",
                          tag: "",
                          price: "R$ 9,90",
                          videoUrl: "",
                          ebookUrl: "",
                          thumb: "",
                          previewUrl: "",
                          checkoutUrl: "",
                          purchased: false,
                        });
                        setGuideFile(null);
                      }}
                      className="bg-gray-100 text-gray-700 font-bold py-2 px-4 rounded-lg"
                    >
                      Limpar
                    </button>
                  </div>
                </div>
                <div className="w-full md:w-64 bg-gray-50 border border-dashed border-gray-200 rounded-xl p-3 text-sm text-gray-500">
                  <p className="font-bold text-gray-700 mb-2">Dica rápida</p>
                  <p>
                    - videoUrl: use o embed do YouTube
                    (https://www.youtube.com/embed/ID)
                  </p>
                  <p>
                    - thumb: use https://img.youtube.com/vi/ID/hqdefault.jpg
                  </p>
                  <p>
                    - ebookUrl: pode ser o caminho no Storage
                    (ebooks/id/arquivo.pdf); upload acima já preenche.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">
                      Guias cadastrados
                    </h3>
                    <p className="text-sm text-gray-500">
                      {guides.length} guias
                    </p>
                  </div>
                  {guideLoading && (
                    <div className="text-xs text-gray-500">Carregando...</div>
                  )}
                </div>
                {guides.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    Nenhum guia. Cadastre o primeiro acima.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {guides.map((g) => (
                      <div
                        key={g.id}
                        className="p-3 bg-gray-50 rounded-lg border flex flex-col md:flex-row md:items-center md:justify-between gap-2"
                      >
                        <div>
                          <p className="font-bold text-gray-900">{g.title}</p>
                          <p className="text-xs text-gray-500">
                            {g.tag} • {g.price}
                          </p>
                          <p className="text-xs text-gray-400 break-all">
                            Video: {g.videoUrl}
                          </p>
                          <p className="text-xs text-gray-400 break-all">
                            Preview: {g.previewUrl}
                          </p>
                          <p className="text-xs text-gray-400 break-all">
                            Checkout: {g.checkoutUrl}
                          </p>
                          <p className="text-xs text-gray-400 break-all">
                            Ebook: {g.ebookUrl}
                          </p>
                          <p className="text-xs text-gray-500">
                            Comprado (flag): {g.purchased ? "sim" : "não"}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setGuideForm({ ...g })}
                            className="bg-blue-500 hover:bg-blue-600 text-white text-sm px-3 py-1 rounded"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => handleDeleteGuide(g.id)}
                            className="bg-red-500 hover:bg-red-600 text-white text-sm px-3 py-1 rounded"
                          >
                            Excluir
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* VIEW: USUÁRIOS */}
          {view === "users" && (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-bold text-gray-800">Usuários</h2>
                <p className="text-sm text-gray-500">Visão de base, status de pagamento e exportação</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                <div className="bg-white border rounded-xl p-3">
                  <p className="text-xs text-gray-500">Usuários totais</p>
                  <p className="text-2xl font-black text-gray-900">{userStatsLoading ? "..." : userStats.total}</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3">
                  <p className="text-xs text-emerald-700">Pagantes</p>
                  <p className="text-2xl font-black text-emerald-700">{userStatsLoading ? "..." : userStats.paid}</p>
                </div>
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
                  <p className="text-xs text-blue-700">Não pagantes</p>
                  <p className="text-2xl font-black text-blue-700">{userStatsLoading ? "..." : userStats.free}</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3">
                  <p className="text-xs text-emerald-700">Pagantes ativos</p>
                  <p className="text-2xl font-black text-emerald-700">{userStatsLoading ? "..." : userStats.paidActive}</p>
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded-xl p-3">
                  <p className="text-xs text-amber-700">Pagantes expirados</p>
                  <p className="text-2xl font-black text-amber-700">{userStatsLoading ? "..." : userStats.paidExpired}</p>
                </div>
              </div>

              <div className="bg-white border rounded-xl p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
                  <h3 className="font-bold text-gray-900">Tabela de usuários</h3>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => setUserFilter("all")} className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${userFilter === "all" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-700"}`}>Todos</button>
                    <button onClick={() => setUserFilter("paid")} className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${userFilter === "paid" ? "bg-emerald-600 text-white" : "bg-emerald-50 text-emerald-700"}`}>Pagantes</button>
                    <button onClick={() => setUserFilter("free")} className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${userFilter === "free" ? "bg-blue-600 text-white" : "bg-blue-50 text-blue-700"}`}>Não pagantes</button>
                    <button onClick={() => setUserFilter("expired")} className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${userFilter === "expired" ? "bg-amber-600 text-white" : "bg-amber-50 text-amber-700"}`}>Expirados</button>
                    <button onClick={handleExportUsersCsv} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-purple-600 text-white hover:bg-purple-700">Exportar CSV</button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="py-2 pr-3">Email</th>
                        <th className="py-2 pr-3">Plano</th>
                        <th className="py-2 pr-3">Status</th>
                        <th className="py-2 pr-3">Expira em</th>
                        <th className="py-2 pr-3">Ciclo</th>
                        <th className="py-2 pr-3">Origem</th>
                        <th className="py-2 pr-3">Últ. pagamento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userStatsLoading ? (
                        <tr><td colSpan={7} className="py-6 text-center text-gray-500">Carregando usuários...</td></tr>
                      ) : filteredUserRows.length === 0 ? (
                        <tr><td colSpan={7} className="py-6 text-center text-gray-500">Nenhum usuário para o filtro selecionado.</td></tr>
                      ) : (
                        filteredUserRows.slice(0, 200).map((row) => (
                          <tr key={row.id} className="border-b last:border-b-0">
                            <td className="py-2 pr-3 text-gray-800">{row.email}</td>
                            <td className="py-2 pr-3">{row.plan}</td>
                            <td className="py-2 pr-3">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${row.status === "ativo" ? "bg-emerald-100 text-emerald-700" : row.status === "expirado" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>
                                {row.status === "nao_pagante" ? "não pagante" : row.status}
                              </span>
                            </td>
                            <td className="py-2 pr-3">{formatDateTime(row.expiresAt)}</td>
                            <td className="py-2 pr-3">{row.billingCycle}</td>
                            <td className="py-2 pr-3">{row.paymentSource}</td>
                            <td className="py-2 pr-3">{row.lastPaymentAmount == null ? "-" : `R$ ${row.lastPaymentAmount.toFixed(2)}`}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  {!userStatsLoading && filteredUserRows.length > 200 && (
                    <p className="text-xs text-gray-500 mt-2">Mostrando 200 de {filteredUserRows.length} usuários. Exporte CSV para ver tudo.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* VIEW: LISTA DE EDITAIS */}
          {view === "list" && (
            <div>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">
                    Editais Cadastrados
                  </h2>
                  <p className="text-sm text-gray-500">
                    {exams.length} editais no banco
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleSeedDatabase}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg transition flex items-center gap-2"
                  >
                    🌱 Popular Banco ({SEED_EXAMS.length} editais)
                  </button>
                  <button
                    onClick={handleAddNew}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2 px-4 rounded-lg transition"
                  >
                    + Novo Edital
                  </button>
                </div>
              </div>


              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                  <p className="text-gray-500">Carregando...</p>
                </div>
              ) : exams.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-xl">
                  <p className="text-4xl mb-4">📋</p>
                  <p className="text-gray-600 font-medium">
                    Nenhum edital cadastrado
                  </p>
                  <p className="text-gray-400 text-sm mt-1">
                    Clique em "Popular Banco" para começar com{" "}
                    {SEED_EXAMS.length} editais
                  </p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {exams.map((exam) => (
                    <div
                      key={exam.id}
                      className="bg-gray-50 p-4 rounded-lg flex flex-col md:flex-row justify-between items-start md:items-center gap-3 border hover:border-emerald-300 transition"
                    >
                      <div>
                        <p className="font-bold text-gray-900">
                          {exam.metadata.institution} - {exam.metadata.role}
                        </p>
                        <p className="text-sm text-gray-600 flex items-center gap-2 flex-wrap">
                          <span>
                            {exam.metadata.banca} • {exam.metadata.year} • {exam.subjects?.length || 0} matérias
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-orange-50 border border-orange-100 text-orange-700 font-semibold">
                            {planCountsLoading ? "..." : `${planCounts[exam.id] ?? 0} planos`}
                          </span>
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${exam.metadata.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-600"}`}
                        >
                          {exam.metadata.active ? "Ativo" : "Inativo"}
                        </span>
                        <button
                          onClick={() => handleEdit(exam)}
                          className="bg-blue-500 hover:bg-blue-600 text-white text-sm py-1 px-3 rounded"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleStartRegenerate(exam)}
                          className="bg-orange-500 hover:bg-orange-600 text-white text-sm py-1 px-3 rounded"
                          title="Regenerar planos de estudo baseados neste edital"
                        >
                          🔄 Planos
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* VIEW: SEED PROGRESS */}
          {view === "seed" && seedingProgress && (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">🌱</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Populando Banco de Dados
              </h2>
              <div className="max-w-md mx-auto">
                <div className="bg-gray-200 rounded-full h-4 mb-4 overflow-hidden">
                  <div
                    className="bg-purple-600 h-full transition-all duration-300"
                    style={{
                      width: `${(seedingProgress.current / seedingProgress.total) * 100}%`,
                    }}
                  />
                </div>
                <p className="text-gray-600 font-medium">
                  {seedingProgress.current} de {seedingProgress.total}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  {seedingProgress.status}
                </p>
              </div>
            </div>
          )}

          {/* VIEW: REGENERATE PLANS */}
          {view === "regenerate" && (
            <div className="text-center py-12">
              {regenerateProgress ? (
                <>
                  <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="text-4xl">🔄</span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Regenerando Planos de Estudo
                  </h2>
                  <div className="max-w-md mx-auto">
                    <div className="bg-gray-200 rounded-full h-4 mb-4 overflow-hidden">
                      <div
                        className="bg-orange-500 h-full transition-all duration-300"
                        style={{
                          width: `${regenerateProgress.total > 0 ? (regenerateProgress.current / regenerateProgress.total) * 100 : 0}%`,
                        }}
                      />
                    </div>
                    <p className="text-gray-600 font-medium">
                      {regenerateProgress.current} de {regenerateProgress.total}
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      {regenerateProgress.email}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="text-4xl">📋</span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Regenerar Planos de Estudo
                  </h2>
                  <p className="text-gray-600 mb-6">
                    Edital:{" "}
                    <strong>
                      {selectedExamForRegenerate?.metadata.institution} -{" "}
                      {selectedExamForRegenerate?.metadata.role}
                    </strong>
                  </p>

                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 max-w-md mx-auto mb-6">
                    <p className="text-3xl font-black text-orange-600 mb-2">
                      {affectedUsersCount}
                    </p>
                    <p className="text-gray-600">usuários serão atualizados</p>
                  </div>

                  {affectedUsersCount === 0 ? (
                    <div className="bg-gray-50 border rounded-xl p-6 max-w-md mx-auto mb-6">
                      <p className="text-gray-600">
                        Nenhum usuário possui plano de estudos baseado neste
                        edital.
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 mb-6">
                      Os planos de estudo desses usuários serão regenerados com
                      base nas novas matérias e tópicos do edital.
                    </p>
                  )}

                  <div className="flex gap-4 justify-center">
                    <button
                      onClick={() => {
                        setView("list");
                        setSelectedExamForRegenerate(null);
                      }}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-3 px-6 rounded-xl transition"
                    >
                      ← Cancelar
                    </button>
                    {affectedUsersCount > 0 && (
                      <button
                        onClick={handleConfirmRegenerate}
                        className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-6 rounded-xl transition"
                      >
                        🔄 Regenerar {affectedUsersCount} planos
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* VIEW: FORMULÁRIO */}
          {view === "form" && (
            <>
              {/* PASSO 1: SELEÇÃO DE ÁREA */}
              {step === "area" && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    Selecione a Área do Concurso
                  </h2>
                  <p className="text-gray-600 mb-6">
                    Isso vai pré-popular as matérias mais comuns dessa área.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {AREAS.map((area) => (
                      <button
                        key={area.id}
                        onClick={() => handleAreaSelect(area.id)}
                        className="p-6 border-2 rounded-xl text-left hover:border-emerald-500 hover:bg-emerald-50 transition group"
                      >
                        <span className="text-3xl mb-2 block">{area.icon}</span>
                        <span className="font-bold text-gray-900 group-hover:text-emerald-700">
                          {area.name}
                        </span>
                      </button>
                    ))}
                  </div>

                  <div className="mt-6 pt-6 border-t">
                    <button
                      onClick={() => {
                        setSelectedArea("");
                        setStep("metadata");
                      }}
                      className="text-gray-500 hover:text-gray-700 font-medium"
                    >
                      Pular seleção de área →
                    </button>
                  </div>
                </div>
              )}

              {/* PASSO 2: METADADOS */}
              {step === "metadata" && (
                <form onSubmit={handleMetadataSubmit} className="space-y-6">
                  <div className="flex items-center gap-2 mb-4">
                    {selectedArea && (
                      <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">
                        {AREAS.find((a) => a.id === selectedArea)?.icon}{" "}
                        {AREAS.find((a) => a.id === selectedArea)?.name}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Instituição (Órgão) *
                      </label>
                      <input
                        type="text"
                        value={metadata.institution}
                        onChange={(e) =>
                          setMetadata({
                            ...metadata,
                            institution: e.target.value,
                          })
                        }
                        placeholder="Ex: INSS, Polícia Federal, TJ-RJ"
                        className="w-full border rounded-lg p-3 focus:ring-2 focus:ring-emerald-500 outline-none"
                        readOnly={!!editingExamId}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Cargo *
                      </label>
                      <input
                        type="text"
                        value={metadata.role}
                        onChange={(e) =>
                          setMetadata({ ...metadata, role: e.target.value })
                        }
                        placeholder="Ex: Técnico, Analista, Agente"
                        className="w-full border rounded-lg p-3 focus:ring-2 focus:ring-emerald-500 outline-none"
                        readOnly={!!editingExamId}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Apelidos / Siglas (separe por vírgula)
                      </label>
                      <input
                        type="text"
                        value={(metadata.aliases || []).join(', ')}
                        onChange={(e) =>
                          setMetadata({
                            ...metadata,
                            aliases: e.target.value
                              .split(',')
                              .map((a) => a.trim())
                              .filter(Boolean),
                          })
                        }
                        placeholder="Ex: PF, PRF, PC, PM"
                        className="w-full border rounded-lg p-3 focus:ring-2 focus:ring-emerald-500 outline-none"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Use siglas e apelidos comuns para facilitar a busca (ex.: PF, PRF, PC, PM).
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Ano *
                      </label>
                      <input
                        type="number"
                        value={metadata.year}
                        onChange={(e) =>
                          setMetadata({
                            ...metadata,
                            year: parseInt(e.target.value),
                          })
                        }
                        className="w-full border rounded-lg p-3 focus:ring-2 focus:ring-emerald-500 outline-none"
                        readOnly={!!editingExamId}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Banca *
                      </label>
                      <select
                        value={metadata.banca}
                        onChange={(e) =>
                          setMetadata({ ...metadata, banca: e.target.value })
                        }
                        className="w-full border rounded-lg p-3 outline-none"
                      >
                        <option value="">Selecione...</option>
                        {BANCAS.map((b) => (
                          <option key={b} value={b}>
                            {b}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Nível
                      </label>
                      <select
                        value={metadata.level}
                        onChange={(e) =>
                          setMetadata({
                            ...metadata,
                            level: e.target.value as "medio" | "superior",
                          })
                        }
                        className="w-full border rounded-lg p-3 outline-none"
                      >
                        <option value="medio">Médio</option>
                        <option value="superior">Superior</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Data da Prova
                      </label>
                      <input
                        type="date"
                        value={metadata.examDate || ""}
                        onChange={(e) =>
                          setMetadata({ ...metadata, examDate: e.target.value })
                        }
                        className="w-full border rounded-lg p-3 focus:ring-2 focus:ring-emerald-500 outline-none"
                      />
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setStep("area")}
                      className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-4 rounded-xl transition"
                    >
                      ← Voltar
                    </button>
                    <button
                      type="submit"
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 rounded-xl transition"
                    >
                      Próximo: Matérias →
                    </button>
                  </div>
                </form>
              )}

              {/* PASSO 3: MATÉRIAS */}
              {step === "subjects" && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">
                        Matérias do Edital
                      </h2>
                      <p className="text-gray-600 text-sm">
                        {selectedArea
                          ? "Matérias sugeridas para a área. Edite conforme necessário."
                          : "Adicione as matérias manualmente."}
                      </p>
                    </div>
                    <button
                      onClick={handleAddSubject}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold"
                    >
                      + Adicionar Matéria
                    </button>
                  </div>

                  <div className="space-y-4 max-h-[400px] overflow-y-auto">
                    {processedSubjects.map((subject, idx) => (
                      <div
                        key={idx}
                        className="bg-gray-50 p-4 rounded-xl border"
                      >
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1">
                            <input
                              type="text"
                              value={subject.name}
                              onChange={(e) =>
                                handleSubjectChange(idx, "name", e.target.value)
                              }
                              placeholder="Nome da matéria"
                              className="w-full font-bold text-gray-900 bg-transparent border-b border-gray-300 pb-1 mb-2 focus:border-emerald-500 outline-none"
                            />
                            <textarea
                              value={subject.topics?.join("\n") || ""}
                              onChange={(e) =>
                                handleSubjectChange(
                                  idx,
                                  "topics",
                                  e.target.value,
                                )
                              }
                              placeholder="Tópicos (um por linha)"
                              rows={3}
                              className="w-full text-sm text-gray-600 bg-white border rounded-lg p-2 focus:ring-2 focus:ring-emerald-500 outline-none"
                            />
                          </div>
                          <button
                            onClick={() => handleRemoveSubject(idx)}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Toggle para usar IA */}
                  <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={useAiProcessing}
                        onChange={(e) => setUseAiProcessing(e.target.checked)}
                        className="w-5 h-5 text-purple-600 rounded"
                      />
                      <div>
                        <span className="font-bold text-purple-800">
                          🤖 Refinar com IA
                        </span>
                        <p className="text-sm text-purple-600">
                          Cole o texto do edital para a IA extrair tópicos
                          detalhados
                        </p>
                      </div>
                    </label>
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={() => setStep("metadata")}
                      className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-4 rounded-xl transition"
                    >
                      ← Voltar
                    </button>
                    <button
                      onClick={handleSubjectsSubmit}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 rounded-xl transition"
                    >
                      {useAiProcessing
                        ? "Próximo: Upload do Edital →"
                        : "Revisar e Salvar →"}
                    </button>
                  </div>
                </div>
              )}

              {/* PASSO 4: CONTEÚDO (PDF/TEXTO) - Opcional */}
              {step === "content" && (
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 p-6 rounded-xl text-center">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept="application/pdf"
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="bg-white border-2 border-blue-200 text-blue-700 font-bold py-3 px-6 rounded-xl hover:bg-blue-50 transition shadow-sm"
                    >
                      📂 Carregar Edital (PDF)
                    </button>
                    <p className="text-sm text-blue-600 mt-2">
                      A IA lerá o PDF e refinará os tópicos
                    </p>
                    {processingStatus && (
                      <p className="text-sm font-bold text-emerald-600 mt-4 animate-pulse">
                        {processingStatus}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Ou cole o texto do edital aqui
                    </label>
                    <textarea
                      value={rawContent}
                      onChange={(e) => setRawContent(e.target.value)}
                      rows={8}
                      placeholder="Cole a seção de conteúdo programático do edital..."
                      className="w-full border rounded-lg p-4 font-mono text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                    />
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={() => setStep("subjects")}
                      className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-4 rounded-xl transition"
                    >
                      ← Voltar
                    </button>
                    <button
                      onClick={() => setStep("review")}
                      className="flex-1 bg-gray-400 hover:bg-gray-500 text-white font-bold py-4 rounded-xl transition"
                    >
                      Pular IA →
                    </button>
                    <button
                      onClick={handleProcessContent}
                      disabled={loading || !rawContent}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 rounded-xl transition flex justify-center items-center gap-2 disabled:opacity-50"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                          Processando...
                        </>
                      ) : (
                        <>
                          <span>🤖</span> Processar com IA
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* PASSO 5: REVISÃO */}
              {step === "review" && (
                <div className="space-y-6">
                  <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                    <h3 className="font-bold text-emerald-800 mb-2">
                      Resumo do Edital
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-sm text-emerald-700">
                      <p>
                        <strong>Concurso:</strong> {metadata.institution} -{" "}
                        {metadata.role}
                      </p>
                      <p>
                        <strong>Banca:</strong> {metadata.banca}
                      </p>
                      <p>
                        <strong>Ano:</strong> {metadata.year}
                      </p>
                      <p>
                        <strong>Matérias:</strong> {processedSubjects.length}
                      </p>
                      <p>
                        <strong>Cargos detectados:</strong> {detectedCargos.length}
                      </p>
                      {metadata.aliases && metadata.aliases.length > 0 && (
                        <p className="col-span-2">
                          <strong>Apelidos:</strong> {metadata.aliases.join(", ")}
                        </p>
                      )}
                      {processingInfo && (
                        <p className="col-span-2 text-emerald-700">
                          <strong>Confiança:</strong>{" "}
                          {processingInfo.truncated
                            ? `Texto truncado (${processingInfo.usedChars}/${processingInfo.totalChars} chars usados)`
                            : `Texto completo (${processingInfo.usedChars || processingInfo.totalChars} chars)`}
                        </p>
                      )}
                    </div>
                  </div>

                  {processingInfo && (
                    <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg text-sm text-purple-800">
                      <div className="flex items-center gap-2 font-bold mb-2">
                        <span>🔍</span> Nível de confiança por matéria
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {processedSubjects.map((subject, idx) => (
                          <span
                            key={idx}
                            className={`px-3 py-1 rounded-full text-xs font-black border ${
                              subject.confidence === "high"
                                ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                                : subject.confidence === "low"
                                ? "bg-amber-100 text-amber-800 border-amber-200"
                                : "bg-purple-100 text-purple-800 border-purple-200"
                            }`}
                          >
                            {subject.name || "Matéria"} — {subject.confidence || "medium"}
                          </span>
                        ))}
                      </div>
                      {processingInfo.truncated && (
                        <p className="mt-2 text-xs text-purple-700">
                          Observação: texto do edital foi truncado para estabilidade da IA.
                        </p>
                      )}
                    </div>
                  )}

                  {detectedCargos.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="font-bold text-blue-900">Validação por cargos (1 cargo = 1 edital derivado)</p>
                          <p className="text-sm text-blue-700">Selecione os cargos que devem virar editais independentes.</p>
                        </div>
                        <div className="flex gap-2">
                          <button type="button" onClick={handleSelectAllCargos} className="px-3 py-1.5 rounded-lg bg-white border border-blue-200 text-blue-700 text-sm font-bold">Selecionar todos</button>
                          <button type="button" onClick={handleClearCargoSelection} className="px-3 py-1.5 rounded-lg bg-white border border-blue-200 text-blue-700 text-sm font-bold">Limpar</button>
                        </div>
                      </div>

                      <div className="space-y-2 max-h-56 overflow-y-auto">
                        {detectedCargos.map((cargo, idx) => {
                          const cargoName = String(cargo?.name || `Cargo ${idx + 1}`);
                          const checked = selectedCargoNames.includes(cargoName);
                          const count = getSubjectsForCargo(cargo, processedSubjects).length;
                          return (
                            <label key={idx} className="flex items-center justify-between gap-3 bg-white border border-blue-100 rounded-lg px-3 py-2">
                              <span className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => handleToggleCargoSelection(cargoName, e.target.checked)}
                                />
                                <span className="font-semibold text-gray-900">{cargoName}</span>
                              </span>
                              <span className="text-xs text-gray-600">{count} matérias</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="max-h-[350px] overflow-y-auto border rounded-lg bg-gray-50">
                    {processedSubjects.map((subject, idx) => (
                      <div
                        key={idx}
                        className="p-4 border-b last:border-0 hover:bg-white transition"
                      >
                        <h4 className="font-bold text-gray-900">
                          {subject.name}
                        </h4>
                        <ul className="mt-2 space-y-1 pl-4 list-disc text-sm text-gray-600">
                          {subject.topics
                            ?.slice(0, 5)
                            .map((t: string, i: number) => (
                              <li key={i}>{t}</li>
                            ))}
                          {subject.topics?.length > 5 && (
                            <li className="list-none text-gray-400 italic">
                              ...e mais {subject.topics.length - 5} tópicos
                            </li>
                          )}
                        </ul>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={() =>
                        setStep(useAiProcessing ? "content" : "subjects")
                      }
                      className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-4 rounded-xl transition"
                    >
                      ← Voltar
                    </button>
                    <button
                      onClick={handleSaveExam}
                      disabled={loading}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 rounded-xl transition"
                    >
                      {loading ? "Salvando..." : "💾 Salvar Edital"}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
